import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import MaterialTable from '@material-table/core';

import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import CustomTableTitle from '../../../partials/CustomTableTitle';
import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import tableColumns from './tableColumns';
import { doesArrayHasLength, isArray } from '../../../utils/isDefined';
import ScheduleDetailView from './ScheduleDetailView';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import { actions as reduxActions } from '../reducers';
import ShowHideButton from '../../../partials/tableCustomButtons/ShowHideButton';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import CustomIconButton from '../../../partials/tableCustomButtons/CustomIconButton';
import { actions as addingSchedulesReduxActions } from '../../../modals/addingSchedules/reducers';
import { getSchedules } from '../actions/getSchedules';
import RefreshButton from '../../../partials/tableCustomButtons/RefreshButton';
import { translate } from '../../../i18n/I18nProvider';

const SchedulesTable = ({
  schedules,
  isLoading,
  columns,
  setColumns,
  clientId,
  openAddingSchedules,
  connectClientId,
  getSchedules
}) => {
  const [filtering, setFiltering] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { localization, language } = useTableLocalization();

  useEffect(() => {
    getSchedules(connectClientId);
    // eslint-disable-next-line
  }, [connectClientId]);

  useEffect(() => {
    if (refresh) {
      getSchedules(connectClientId);
      setRefresh(false);
    }
    // eslint-disable-next-line
  }, [refresh]);

  const tableDefaultColumns = useMemo(() => {
    return tableColumns({ clientId });
  }, [clientId]);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'Schedules-1',
    setColumns,
    tableDefaultColumns,
    language
  });

  const schedulesToShow = useMemo(() => {
    if (!isArray(schedules)) {
      return [];
    }
    if (showAll) {
      return schedules;
    } else {
      return schedules.filter(schedule => schedule.schedules.length > 1);
    }
  }, [schedules, showAll]);

  const tableOptions = useMemo(() => {
    return {
      ...mapTableOptions({ exportData: false }),
      filtering,
      selection: true,
      header: doesArrayHasLength(schedules),
      headerStyle: {
        zIndex: 2
      },
      pageSize
    };
  }, [filtering, schedules, pageSize]);

  const actions = useMemo(
    () => [
      {
        icon: () => <ShowHideButton show={showAll} />,
        onClick: () => {
          setShowAll(!showAll);
        },
        isFreeAction: true,
        tooltip: translate('TOOLTIP.SHOW_HIDDEN_SHEDULES')
      },
      {
        icon: () => <FilterButton />,
        onClick: () => {
          setFiltering(!filtering);
        },
        isFreeAction: true,
        tooltip: translate('BUTTON.FILTER')
      },
      {
        tooltip: translate('EDIT_SCHEDULE.ADD_NEW'),
        icon: () => <CustomIconButton iconClassName="far fa-plus" />,
        onClick: (evt, data) =>
          openAddingSchedules({
            schedules: data
          })
      },
      {
        icon: () => <RefreshButton tableHeader={true} />,
        onClick: () => setRefresh(true),
        isFreeAction: true
      }
    ],
    // eslint-disable-next-line
    [filtering, showAll]
  );

  const detailPanel = useCallback(
    ({ rowData }) => (
      <ScheduleDetailView data={rowData.schedules} group={rowData} />
    ),
    []
  );

  return (
    <MaterialTable
      title={
        <CustomTableTitle
          text="MENU.SCHEDULES"
          icon={<i className="fas fa-calendar-alt"></i>}
          isLoading={isLoading}
        />
      }
      icons={customTableIcons}
      actions={actions}
      columns={columns}
      data={schedulesToShow}
      options={tableOptions}
      detailPanel={detailPanel}
      localization={localization}
      onChangeColumnHidden={onChangeColumnHidden}
      onRowsPerPageChange={onChangeRowsPerPage}
    />
  );
};

const mapStatesToProps = ({ schedules, selectClient, loader }) => {
  return {
    ...schedules,
    isLoading: loader.isSpinVisible,
    clientId: selectClient.selectedClient?.id,
    connectClientId: selectClient.selectedClient?.connect30_domain
  };
};

const mapDispatchToProps = {
  openAddingSchedules: addingSchedulesReduxActions.openDiaog,
  setColumns: reduxActions.setColumns,
  getSchedules
};

export default connect(mapStatesToProps, mapDispatchToProps)(SchedulesTable);
